import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { type UserData, type UserDataContextType, defaultUserData } from '@/types/userData';

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

interface UserDataProviderProps {
  children: ReactNode;
}

export const UserDataProvider = ({ children }: UserDataProviderProps) => {
  const [userData, setUserData] = useState<UserData>(defaultUserData);

  const updateUserData = useCallback((updates: Partial<UserData>) => {
    setUserData(prev => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetUserData = useCallback(() => {
    setUserData(defaultUserData);
  }, []);

  const value: UserDataContextType = {
    userData,
    updateUserData,
    resetUserData,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = (): UserDataContextType => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};
